import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { GUIDE_CATEGORIES } from '@/data/guides';
import { motion } from 'motion/react';
import { Info, AlertCircle, Lightbulb, ChevronRight, Share2, Printer, BookOpen, HelpCircle, FileText, Bell, Mail, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SEO from '@/components/SEO';
import { DiscordCTA } from '@/components/DiscordCTA';

const GuideImage = ({ baseUrl, name, alt }: { baseUrl: string; name: string; alt: string }) => {
  const [format, setFormat] = useState<'webp' | 'gif'>('webp');
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const path = `${baseUrl}/${name}.${format}`;

  const handleError = () => {
    if (format === 'webp') {
      setFormat('gif');
    } else {
      setError(true);
    }
  };

  if (error) return null;

  return (
    <div className={`my-8 rounded-2xl overflow-hidden border border-border bg-muted/30 transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
      <img 
        src={path} 
        alt={alt}
        className="w-full h-auto object-cover"
        onError={handleError}
        onLoad={() => setLoaded(true)}
      />
      <div className="px-4 py-3 border-t border-border bg-muted/20">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{alt}</p>
      </div>
    </div>
  );
};

const HelpDetail = () => {
  const { category: categoryId, slug } = useParams();

  const category = GUIDE_CATEGORIES.find(c => c.id === categoryId);
  const article = category?.articles.find(a => a.slug === slug);

  if (!category || !article) {
    return <Navigate to="/help" replace />;
  }

  // Define the base path for images in the bucket
  const imageBaseUrl = `https://storage.googleapis.com/ais-studio-public/guides/${categoryId}/${slug}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-12"
    >
      <SEO 
        title={`${article.title} | Help Center`} 
        description={article.description}
      />

      {/* Header */}
      <header className="space-y-6">
        <nav className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
          <Link to="/help" className="hover:text-foreground transition-colors">Help</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-primary/70">{category.title}</span>
        </nav>
        <div className="space-y-4">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-foreground">
            {article.title}
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl">
            {article.description}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 pt-4">
          <Button variant="outline" size="sm" className="rounded-full gap-2 text-xs" onClick={() => window.print()}>
            <Printer className="w-3.5 h-3.5" />
            Print Guide
          </Button>
          <Button variant="outline" size="sm" className="rounded-full gap-2 text-xs">
            <Share2 className="w-3.5 h-3.5" />
            Copy Link
          </Button>
        </div>
      </header>

      {/* Table of Contents (Auto-generated from sections) */}
      {article.sections.length > 1 && (
        <div className="p-6 rounded-2xl bg-muted/30 border border-border">
          <h3 className="text-xs font-black uppercase tracking-widest mb-4 text-foreground/50">On this page</h3>
          <ul className="space-y-2">
            {article.sections.map(section => (
              <li key={section.id}>
                <a 
                  href={`#${section.id}`} 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 group"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-primary/20 group-hover:bg-primary transition-colors" />
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Content Sections */}
      <div className="space-y-20">
        {article.sections.map((section, idx) => (
          <section key={section.id} id={section.id} className="scroll-mt-28">
            <div className="flex items-center gap-4 mb-6">
              <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-black text-xs">
                {(idx + 1).toString().padStart(2, '0')}
              </span>
              <h2 className="text-2xl font-bold tracking-tight text-foreground">
                {section.title}
              </h2>
            </div>

            <div className="prose prose-invert max-w-none text-muted-foreground leading-relaxed space-y-4">
              <p className="whitespace-pre-wrap">{section.content}</p>
            </div>

            {/* Conditional Image */}
            <GuideImage 
              baseUrl={imageBaseUrl}
              name={section.settingId || section.id}
              alt={`${section.title} visual reference`}
            />

            {/* Technical Details */}
            {section.technicalDetails && (
              <div className="mt-6 p-5 rounded-xl bg-foreground/[0.02] border border-foreground/[0.05] space-y-3">
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-foreground/40">
                  <Info className="w-3 h-3" />
                  Technical Parameters
                </div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {section.technicalDetails.map((detail, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <div className="mt-1 w-1 h-1 rounded-full bg-muted-foreground/30 flex-shrink-0" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Pro Tips */}
            {section.tips && (
              <div className="mt-6 p-5 rounded-xl bg-primary/5 border border-primary/10 space-y-3">
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-primary/60">
                  <Lightbulb className="w-3 h-3" />
                  Pro Tip
                </div>
                {section.tips.map((tip, i) => (
                  <p key={i} className="text-sm text-foreground/80 italic pl-5 relative before:content-['''] before:absolute before:left-0 before:text-primary before:text-2xl before:leading-none before:font-serif">
                    {tip}
                  </p>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>

      {/* Footer Navigation */}
      <footer className="pt-20 pb-12 border-t border-border flex justify-between items-center text-sm">
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground uppercase font-black tracking-widest">Previous</p>
          <Link to="/help" className="text-primary font-bold hover:underline">Help Main</Link>
        </div>
        <div className="flex flex-col items-end gap-2">
          <p className="text-xs text-muted-foreground uppercase font-black tracking-widest">Next Guide</p>
          <Link to="/help" className="text-primary font-bold hover:underline">Browse All Guides</Link>
        </div>
      </footer>

      {/* Helpful Resources */}
      <section className="pt-12 border-t border-border space-y-6">
        <h3 className="text-lg font-bold font-display uppercase tracking-tight text-foreground">Helpful Resources</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <ResourceLink icon={<BookOpen className="w-4 h-4" />} title="Creator Tutorials" description="Step-by-step guides for all skill levels" to="/tutorials" />
          <ResourceLink icon={<HelpCircle className="w-4 h-4" />} title="FAQ" description="100+ commonly asked questions answered" to="/faq" />
          <ResourceLink icon={<FileText className="w-4 h-4" />} title="Blog" description="Latest insights and creator strategies" to="/blog" />
          <ResourceLink icon={<Bell className="w-4 h-4" />} title="Updates" description="New features and engine improvements" to="/updates" />
          <ResourceLink icon={<Mail className="w-4 h-4" />} title="Contact Support" description="Get help from our team directly" to="/contact" />
          <a href="https://discord.gg/cdwNFdbx" target="_blank" rel="noopener noreferrer" className="p-4 rounded-2xl bg-[#5865F2]/5 border border-[#5865F2]/10 flex items-center gap-3 group hover:border-[#5865F2]/30 transition-all">
            <div className="w-8 h-8 rounded-xl bg-[#5865F2]/10 flex items-center justify-center text-[#5865F2] group-hover:bg-[#5865F2] group-hover:text-white transition-all">
              <MessageSquare className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-[#5865F2]">Discord Community</h4>
              <p className="text-xs text-muted-foreground">Beta testing, bugs & features</p>
            </div>
          </a>
        </div>
      </section>

      <DiscordCTA variant="banner" />
    </motion.div>
  );
};

const ResourceLink = ({ icon, title, description, to }: { icon: React.ReactNode; title: string; description: string; to: string }) => (
  <Link to={to} className="p-4 rounded-2xl bg-foreground/[0.02] border border-foreground/[0.05] flex items-center gap-3 group hover:border-primary/30 transition-all">
    <div className="w-8 h-8 rounded-xl bg-primary/5 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all">
      {icon}
    </div>
    <div>
      <h4 className="text-sm font-bold">{title}</h4>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  </Link>
);

export default HelpDetail;
